import { useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { format, isValid, subDays, parse, isAfter, isBefore } from 'date-fns';
import { trackEvent } from '@surfline/web-common';

export const checkStartDateIsValid = (date: string | undefined) => {
  if (typeof date !== 'string') return false;

  const parsedDate = parse(date, 'yyyy-MM-dd', new Date());
  return Boolean(
    isValid(parsedDate) &&
      isAfter(parsedDate, new Date('2010-01-01')) &&
      isBefore(parsedDate, subDays(new Date(), 1)),
  );
};

export const updateURLWithHistoricStartDate = async (
  router: ReturnType<typeof useRouter>,
  type: 'add' | 'delete',
  start?: string,
) => {
  if (!router.isReady || router.isFallback) return;

  const currentQuery = { ...router.query };

  if (type === 'add' && start && checkStartDateIsValid(start)) {
    currentQuery.startDate = start;
  } else {
    delete currentQuery.startDate;
  }

  // Construct the new URL with updated query params
  const newUrl = {
    pathname: router.pathname,
    query: currentQuery,
  };

  try {
    await router.replace(newUrl, undefined, {
      shallow: true,
      scroll: false,
    });
  } catch (error) {
    console.warn('Failed to update URL with historic start date:', error);
  }
};

export interface UpdateHistoricForecastURL {
  isPermitted: boolean;
  segmentSpotDetails: {
    spotId: string;
    spotName: string;
    subregionId: string;
    subregionName: string;
  };
}

const useUpdateHistoricForecastURL = ({
  isPermitted,
  segmentSpotDetails,
}: UpdateHistoricForecastURL) => {
  const { spotId, spotName, subregionId, subregionName } = segmentSpotDetails;
  const router = useRouter();

  const startInHistoricView = Boolean(
    isPermitted &&
      typeof router?.query?.startDate === 'string' &&
      checkStartDateIsValid(router?.query?.startDate),
  );

  const [startDate, setStartDate] = useState(
    startInHistoricView ? router?.query?.startDate?.toString() : undefined,
  );
  const [isHistoricView, setIsHistoricView] = useState(startInHistoricView);

  const showHistoricForecast = !!(isPermitted && isHistoricView && startDate);

  const handleSetStartDate = useCallback(
    async (newStartDate: Date | null) => {
      if (isPermitted) {
        const formattedDate = format(newStartDate || 0, 'yyyy-MM-dd');
        setStartDate(formattedDate);

        await updateURLWithHistoricStartDate(router, 'add', formattedDate);

        trackEvent('Forecast Range Updated', {
          action: 'click',
          uiElement: 'historic-forecast',
          forecastYear: format(newStartDate || 0, 'yyyy'),
          spotId,
          spotName,
          subregionId,
          subregionName,
        });
      }
    },
    [isPermitted, router, spotId, spotName, subregionId, subregionName],
  );

  const toggleHistoricView = useCallback(async () => {
    if (startDate && isHistoricView) {
      setStartDate(undefined);
      await updateURLWithHistoricStartDate(router, 'delete');
    }

    setIsHistoricView(!isHistoricView);
  }, [isHistoricView, router, startDate]);

  return {
    handleSetStartDate,
    isHistoricView,
    showHistoricForecast,
    startDate,
    toggleHistoricView,
  };
};

export default useUpdateHistoricForecastURL;
